import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from 'next-themes';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import NotificationBell from './NotificationBell';
import MaintenanceBanner from './MaintenanceBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from './ui/dropdown-menu';
import {
  Package,
  Warehouse,
  ShoppingCart,
  ReceiptText,
  Receipt,
  TrendingUp,
  Users,
  FileText,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  User as UserIcon,
  ClipboardList,
  FileCheck,
  PackageCheck,
  Shield,
  GitBranch,
  BookOpen,
  Building2,
  HeartPulse,
  HandHeart,
  Undo2,
  Building,
  Pill,
  Trash2,
  Wrench,
  UserSquare2,
  Briefcase,
  ShieldPlus,
  Wallet,
  LayoutDashboard,
  ArrowLeftRight,
  ClipboardCheck,
  BarChart3,
  AlertTriangle,
  Settings,
  ChevronDown,
  ChevronRight,
  BookText,
  Scale,
  Lock,
  FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para verificar permisos
const userCan = (user, module, action = 'ver') => {
  if (!user) return false;
  // Admin siempre tiene acceso
  if (user.role === 'admin') return true;
  // Si tiene permisos personalizados, usarlos
  if (user.permissions) {
    return user.permissions[module]?.[action] === true;
  }
  // Si no, usar permisos por defecto del rol (simplificado)
  if (user.role === 'supervisor') {
    const supervisorDefaults = {
      dashboard: { ver: true },
      movimiento_interno: { ver: true },
      notas_pedido: { ver: true, crear: true, aprobar: true },
      ordenes_compra: { ver: true },
      recepciones: { ver: true, crear: true }
    };
    return supervisorDefaults[module]?.[action] === true;
  }
  // Operador por defecto
  const operatorDefaults = {
    dashboard: { ver: true },
    notas_pedido: { ver: true, crear: true },
    // Módulo Residentes - Operador puede VER por defecto
    residentes: { ver: true },
    servicios_residentes: { ver: true }
  };
  return operatorDefaults[module]?.[action] === true;
};

function Layout({ user, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [appVersion, setAppVersion] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    fetch('/docs/docs.json', { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setAppVersion({ version: d.version, date: d.date }))
      .catch(() => {});
  }, []);

  // Aviso global de Modo Mantenimiento en cualquier acción bloqueada (423)
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        const status = error?.response?.status;
        if (status === 423) {
          toast.error(error.response.data?.detail || 'El sistema está en mantenimiento. Intentá más tarde.');
        } else if (status === 401 && !window.location.pathname.startsWith('/login')) {
          toast.error('Tu sesión se cerró. Iniciá sesión nuevamente.');
          setTimeout(() => { window.location.href = '/login'; }, 900);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // ============== ESTRUCTURA AGRUPADA DE NAVEGACIÓN ==============
  // Cada grupo: { id, name, icon, items: [{ name, icon, path }] }
  // El "Tablero" va suelto al inicio (sin grupo).
  const groups = [
    // 🧑‍🦽 RESIDENTES
    {
      id: 'residentes',
      name: 'Residentes',
      icon: UserSquare2,
      items: [
        userCan(user, 'residentes', 'ver') && { name: 'Ficha Residente', icon: UserSquare2, path: '/residents' },
        userCan(user, 'residentes', 'ver') && { name: 'Reportes', icon: FileSpreadsheet, path: '/residents/reports' },
        userCan(user, 'servicios_residentes', 'ver') && { name: 'Catálogo Servicios', icon: Briefcase, path: '/resident-services' },
        userCan(user, 'servicios_residentes', 'ver') && { name: 'Convenios', icon: ShieldPlus, path: '/health-insurers' },
        userCan(user, 'residentes', 'ver') && { name: 'Facturación', icon: FileText, path: '/billing' },
        userCan(user, 'deudores', 'ver') && { name: 'Deudores por Ventas', icon: AlertTriangle, path: '/billing/debtors' },
      ].filter(Boolean),
    },
    // 💰 TESORERÍA
    {
      id: 'tesoreria',
      name: 'Tesorería',
      icon: Wallet,
      items: [
        (user?.role === 'admin' || userCan(user, 'tesoreria_panel', 'ver') || userCan(user, 'tesoreria_config', 'ver')) &&
          { name: 'Panel', icon: LayoutDashboard, path: '/treasury' },
        (user?.role === 'admin' || ['tesoreria_cobranzas','tesoreria_pagos','tesoreria_transferencias','tesoreria_ajustes','tesoreria_valores','tesoreria_libro'].some(m => userCan(user, m, 'ver'))) &&
          { name: 'Operaciones', icon: ArrowLeftRight, path: '/treasury/operations' },
        (user?.role === 'admin' || ['tesoreria_arqueos','tesoreria_conciliaciones'].some(m => userCan(user, m, 'ver'))) &&
          { name: 'Control', icon: ClipboardCheck, path: '/treasury/control' },
        (user?.role === 'admin' || userCan(user, 'tesoreria_reportes', 'ver')) &&
          { name: 'Reportes', icon: BarChart3, path: '/treasury/reports' },
      ].filter(Boolean),
    },
    // 🛒 COMPRAS — operación de compras
    {
      id: 'compras',
      name: 'Compras',
      icon: ShoppingCart,
      items: [
        userCan(user, 'proveedores', 'ver') && { name: 'Proveedores', icon: Building2, path: '/suppliers' },
        userCan(user, 'notas_pedido', 'ver') && { name: 'Notas de Pedido', icon: ClipboardList, path: '/purchase-requests' },
        userCan(user, 'demanda_insatisfecha', 'ver') && { name: 'Demanda Insatisfecha', icon: AlertTriangle, path: '/purchases/unmet-demand' },
        userCan(user, 'ordenes_compra', 'ver') && { name: 'Órdenes de Compra', icon: FileCheck, path: '/purchase-orders' },
        userCan(user, 'recepciones', 'ver') && { name: 'Recepciones', icon: PackageCheck, path: '/receptions' },
        userCan(user, 'compras_otras', 'ver') && { name: 'Otras Compras', icon: ShoppingCart, path: '/other-purchases' },
        userCan(user, 'compras_otras', 'ver') && { name: 'Comprobantes Fiscales', icon: ReceiptText, path: '/purchases/fiscal-documents' },
        userCan(user, 'proveedores_cc', 'ver') && { name: 'Cta. Corriente Proveedores', icon: Wallet, path: '/supplier-accounts' },
      ].filter(Boolean),
    },
    // 📦 STOCK Y MOVIMIENTOS
    {
      id: 'stock',
      name: 'Stock y Movimientos',
      icon: Package,
      items: [
        userCan(user, 'depositos', 'ver') && { name: 'Stock por Depósito', icon: Warehouse, path: '/warehouses' },
        userCan(user, 'movimiento_interno', 'ver') && { name: 'Movimiento Interno', icon: ShoppingCart, path: '/movements' },
        userCan(user, 'transferencias_deposito', 'ver') && { name: 'Transferencias entre Depósitos', icon: ArrowLeftRight, path: '/warehouse-transfers' },
        userCan(user, 'conteo_fisico', 'ver') && { name: 'Conteo Físico', icon: ClipboardCheck, path: '/stock-counts' },
      ].filter(Boolean),
    },
    // 💊 ENFERMERÍA
    {
      id: 'enfermeria',
      name: 'Enfermería',
      icon: HeartPulse,
      items: [
        (userCan(user, 'pedidos_internos', 'ver') || userCan(user, 'recepciones_internas', 'ver')) && { name: 'Stock del Área', icon: Pill, path: '/nursing-stock' },
        userCan(user, 'pedidos_internos', 'ver') && { name: 'Pedidos', icon: ClipboardList, path: '/internal-requests' },
        userCan(user, 'pedidos_internos', 'ver') && { name: 'Devoluciones del Área', icon: Undo2, path: '/internal-returns' },
        userCan(user, 'pedidos_internos', 'consumir') && { name: 'Consumo Interno', icon: Pill, path: '/internal-consumptions' },
        userCan(user, 'conteo_fisico_area', 'ver') && { name: 'Arqueo del Área', icon: ClipboardCheck, path: '/area-stock-counts' },
        userCan(user, 'pedidos_internos', 'ver') && { name: 'Transferencias entre Áreas', icon: ArrowLeftRight, path: '/area-transfers' },
        userCan(user, 'recepciones_internas', 'ver') && { name: 'Ingresos de Familiares (REM)', icon: HandHeart, path: '/nursing-receptions' },
      ].filter(Boolean),
    },
    // 📒 CONTABILIDAD (Fase 2)
    {
      id: 'contabilidad',
      name: 'Contabilidad',
      icon: BookText,
      items: [
        userCan(user, 'contabilidad', 'ver') && { name: 'Plan de Cuentas', icon: BookText, path: '/accounting/chart-of-accounts' },
        userCan(user, 'contabilidad', 'ver') && { name: 'Libro Diario', icon: BookOpen, path: '/accounting/journal' },
        userCan(user, 'contabilidad', 'ver') && { name: 'Libro Mayor', icon: FileText, path: '/accounting/ledger' },
        userCan(user, 'contabilidad', 'ver') && { name: 'Sumas y Saldos', icon: BarChart3, path: '/accounting/trial-balance' },
        userCan(user, 'contabilidad_iva', 'ver') && { name: 'Libro IVA Ventas', icon: ReceiptText, path: '/accounting/iva-sales-book' },
        userCan(user, 'contabilidad_iva', 'ver') && { name: 'Libro IVA Compras', icon: ShoppingCart, path: '/accounting/iva-purchase-book' },
        userCan(user, 'contabilidad_iva', 'ver') && { name: 'Posición de IVA', icon: Scale, path: '/accounting/iva-position' },
        (user?.role === 'admin' || userCan(user, 'contabilidad', 'ver')) && { name: 'Cierres contables', icon: Lock, path: '/accounting/closures' },
      ].filter(Boolean),
    },
    // 📊 ANÁLISIS (Reportes + Trazabilidad)
    {
      id: 'analisis',
      name: 'Análisis',
      icon: TrendingUp,
      items: [
        userCan(user, 'reportes', 'ver') && { name: 'Reportes', icon: FileText, path: '/reports' },
        userCan(user, 'reportes', 'ver') && { name: 'Trazabilidad', icon: GitBranch, path: '/traceability' },
      ].filter(Boolean),
    },
    // ⚙️ CONFIGURACIÓN
    {
      id: 'configuracion',
      name: 'Configuración',
      icon: Settings,
      items: [
        user?.role === 'admin' && { name: 'Depósitos', icon: Warehouse, path: '/warehouses' },
        user?.role === 'admin' && { name: 'Facturación ARCA', icon: Receipt, path: '/arca-config' },
        user?.role === 'admin' && { name: 'Configuración de Áreas', icon: Building, path: '/internal-areas' },
        // v3.30.15 · Ocultar el ítem cuando el usuario sólo tiene `.ver` sin
        // capacidad real de operar. `articulos.ver` es una silent-dependency
        // que muchos módulos (Compras, Depósitos, Subdepósitos, Enfermería) ya
        // requieren para completar sus selects — sin `.crear` el usuario abre
        // el catálogo y ve "no hay artículos" (confusión + soporte innecesario).
        // v3.40.1 · También aparece si el usuario tiene `articulos.estabilizar`
        // (acceso delegado al tab de Homologación & Fusión sin ser admin ni
        // tener el resto de los permisos de Configuración).
        (user?.role === 'admin'
          || userCan(user, 'articulos', 'crear')
          || !!user?.permissions?.articulos?.estabilizar) &&
          { name: 'Artículos', icon: Package, path: '/articles' },
        userCan(user, 'usuarios', 'ver') && { name: 'Usuarios', icon: Users, path: '/users' },
        userCan(user, 'auditoria', 'ver') && { name: 'Auditoría', icon: Shield, path: '/audit' },
        user?.role === 'admin' && { name: 'Centro de Mantenimiento', icon: Wrench, path: '/maintenance-center' },
        user?.role === 'admin' && { name: 'Purga (Test)', icon: Trash2, path: '/maintenance' },
      ].filter(Boolean),
    },
  ].filter(g => g.items.length > 0);

  // ============== Estado colapsable persistente ==============
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('sidebar_open_groups_v1');
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return {};
  });

  // Auto-abrir el grupo que contenga la ruta activa
  useEffect(() => {
    const activeGroup = groups.find(g => g.items.some(it => it.path === location.pathname));
    if (activeGroup && !openGroups[activeGroup.id]) {
      setOpenGroups(prev => ({ ...prev, [activeGroup.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setOpenGroups(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('sidebar_open_groups_v1', JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Sesión cerrada');
      navigate('/login');
    } catch (error) {
      toast.error('Error al cerrar sesión');
    }
  };
  
  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 256 : 0 }}
        transition={{ duration: 0.3 }}
        className="bg-card border-r border-border overflow-hidden"
      >
        <div className="flex flex-col h-full w-64">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-border">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/logo-hogar.png" alt="Hogar Gestión" className="h-10" />
              <span className="font-bold text-lg tracking-tight">Hogar Gestión</span>
            </Link>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {/* Tablero - siempre arriba, fuera de grupos */}
            <Link
              to="/"
              data-testid="nav-tablero"
              className={`flex items-center space-x-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                location.pathname === '/'
                  ? 'bg-blue-600 text-white'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <TrendingUp className="w-5 h-5" />
              <span>Tablero</span>
            </Link>

            {groups.map((group) => {
              const isOpen = !!openGroups[group.id];
              const hasActive = group.items.some(it => it.path === location.pathname);
              return (
                <div key={group.id} className="pt-2">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    data-testid={`sidebar-group-${group.id}`}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-colors ${
                      hasActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                    } hover:bg-accent/50`}
                  >
                    <span className="flex items-center gap-2">
                      <group.icon className="w-4 h-4" />
                      {group.name}
                    </span>
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  {isOpen && (
                    <div className="mt-1 ml-2 pl-3 border-l border-border space-y-1">
                      {group.items.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                          <Link
                            key={`${group.id}-${item.path}`}
                            to={item.path}
                            data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                          >
                            <item.icon className="w-4 h-4" />
                            <span>{item.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </motion.aside>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          
          <div className="flex items-center space-x-4">
            {/* Notification Bell */}
            <NotificationBell />

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-trigger">
                  <Avatar>
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback>{user?.name?.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <p className="text-xs text-blue-600 font-medium uppercase">
                      {user?.role === 'operator' ? 'OPERADOR' : user?.role === 'supervisor' ? 'SUPERVISOR' : 'SUPER ADMIN'}
                    </p>
                    <p className="text-[10px] font-mono text-muted-foreground pt-1" data-testid="dropdown-app-version">
                      {appVersion ? `${appVersion.version} · ${appVersion.date}` : ''}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                  <UserIcon className="mr-2 h-4 w-4" />
                  <span>Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/help/formal')} data-testid="manual-menu-item">
                  <BookOpen className="mr-2 h-4 w-4 text-blue-600" />
                  <div className="flex flex-col">
                    <span>Manual de Usuario</span>
                    {appVersion && <span className="text-[10px] text-muted-foreground font-mono">{appVersion.version} · {appVersion.date}</span>}
                  </div>
                </DropdownMenuItem>
                {/* v3.41.1 — Guía reubicada: ahora se accede desde /articles → pestaña Homologación & Fusión (panel plegable al inicio del tab). */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="dev-notes-submenu-trigger">
                    <FileText className="mr-2 h-4 w-4 text-slate-500" />
                    <span>Notas del Desarrollador</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => navigate('/help/manual')} data-testid="dev-notes-changelog-item">
                        <FileText className="mr-2 h-4 w-4 text-slate-500" />
                        <div className="flex flex-col">
                          <span>Reseñas Desarrollo</span>
                          <span className="text-[10px] text-muted-foreground">Cambios técnicos por versión</span>
                        </div>
                      </DropdownMenuItem>
                      {user?.role === 'admin' && (
                        <DropdownMenuItem onClick={() => navigate('/help/executive')} data-testid="dev-notes-executive-item">
                          <FileText className="mr-2 h-4 w-4 text-rose-600" />
                          <div className="flex flex-col">
                            <span>Documento Ejecutivo</span>
                            <span className="text-[10px] text-muted-foreground">Estrategia · Admin</span>
                          </div>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Cerrar sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Aviso global de Modo Mantenimiento */}
        <MaintenanceBanner isAdmin={user?.role === 'admin'} />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}

export default Layout;